import { useState, type ReactNode } from "react"
import { formatCurrencyWithNumberToSymbolWithNumber as money } from "@ui/utils/formatter"
import styled from "styled-components"
import { T } from "../adminTheme"
import { Paged } from "./adminUi"

const MUTED = T.muted
const BORDER = T.border
const MAX_TABLE_COLUMNS = 8
/** Levels deeper than this start collapsed. */
const OPEN_DEPTH = 2
const MIN_TABLE_ROWS = 2
const GOOD = ["active", "approved", "verified", "confirmed", "credited", "completed", "complete", "success", "succeeded", "settled", "done", "swept", "ok", "enabled", "paid", "issued", "yes", "ready for cards", "can be copied", "matched", "healthy", "usable"]
const BAD = ["disabled", "blocked", "rejected", "failed", "declined", "deleted", "closed", "error", "expired", "cancelled", "canceled", "frozen", "suspended", "empty", "dry", "unavailable", "mismatch", "missing", "critical", "fatal"]
const ACRONYMS: Record<string, string> = { uid: "UID", id: "ID", kyc: "KYC", vat: "VAT", bin: "BIN", url: "URL", api: "API", iban: "IBAN", ip: "IP", otp: "OTP", trx: "TRX", tx: "TX", mcc: "MCC", txn: "TXN", pan: "PAN", cvv: "CVV", sms: "SMS", usd: "USD", eur: "EUR" }
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/
const isStatusKey = (key: string) => /(^|_)(status|state|result|level|stage)$/.test(key)
const isCodeKey = (key: string) => /(^|_)(uid|id|hash|uuid|address|token|key|number|code)$/.test(key)
const isEmpty = (value: unknown) => value === null || value === undefined || value === "" ||
	(typeof value === "object" && Object.keys(value as object).length === 0)

/** "provider_holder_id" → "Provider holder ID" */
const humanize = (key: string) => {
	// Money in *_cents is already shown formatted, so the unit suffix is noise.
	const words = key.replace(/_cents$/, "").replace(/([a-z])([A-Z])/g, "$1_$2").split(/[_\s.-]+/).filter(Boolean)
	return words.map((word, index) => ACRONYMS[word.toLowerCase()] ?? (index ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1).toLowerCase())).join(" ")
}

type Tone = "good" | "muted" | "bad" | "warn"
const tone = (value: string): Tone => {
	const key = value.toLowerCase()
	if (["no", "none", "unknown", "n/a", "not_started", "info", "debug", "notice"].includes(key)) return "muted"
	// Phrases like "KYC declined" or "Ready for cards" are judged by their words, bad words first.
	const words = key.split(/[\s_-]+/)
	if (BAD.includes(key) || words.some((word) => BAD.includes(word))) return "bad"
	if (GOOD.includes(key) || words.some((word) => GOOD.includes(word) || word === "ready")) return "good"
	return "warn"
}
/** Sentence case for display only; the DOM keeps the raw status text. */
const Cap = styled.span`display: inline-block; &::first-letter { text-transform: uppercase; }`
const PILL = { bad: [T.danger, T.dangerSoft, T.dangerLine], warn: [T.warn, T.warnSoft, T.warnLine] } as const
const dot = (color: string) => <i aria-hidden style={{ width: 6, height: 6, borderRadius: 6, background: color, flex: "none" }} />
export const StatusBadge = ({ value }: { value?: string | null }) => {
	if (!value) return <span style={{ color: MUTED }}>—</span>
	const kind = tone(value)
	const label = <Cap>{value.replaceAll("_", " ")}</Cap>
	const base = { display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", whiteSpace: "nowrap" } as const
	// Calm by default: healthy and neutral states are quiet text; only problems get a coloured pill.
	if (kind === "muted") return <span style={{ ...base, color: MUTED }}>{label}</span>
	if (kind === "good") return <span style={base}>{dot(T.success)}{label}</span>
	const [color, background, line] = PILL[kind]
	return <span style={{ ...base, padding: "1px 8px", borderRadius: 999, fontSize: 12.5, fontWeight: 550, color, background, boxShadow: `inset 0 0 0 1px ${line}` }}>{dot(color)}{label}</span>
}

const parseJson = (value: unknown) => {
	if (typeof value !== "string" || !/^[[{]/.test(value.trim())) return value
	try { return JSON.parse(value) as unknown } catch { return value }
}

const Scalar = ({ name, value }: { name: string; value: unknown }) => {
	if (isEmpty(value)) return <span style={{ color: MUTED }}>—</span>
	if (typeof value === "boolean") return <StatusBadge value={value ? "Yes" : "No"} />
	const text = String(value)
	if (isStatusKey(name) || GOOD.includes(text.toLowerCase()) || BAD.includes(text.toLowerCase())) return <StatusBadge value={text} />
	if (/_cents$/.test(name) && typeof value === "number") return <>{money(value, "USD")}</>
	if (ISO_DATE.test(text) && !Number.isNaN(Date.parse(text))) return <span title={text}>{new Date(text).toLocaleString()}</span>
	if (isCodeKey(name)) return <code style={{ fontSize: 12, background: T.hover, padding: "1px 6px", borderRadius: 5, overflowWrap: "anywhere" }}>{text}</code>
	return <span style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{text}</span>
}

const isPlain = (value: unknown) => value === null || typeof value !== "object"
/** A list of same-shaped flat records reads best as a table. */
const asTable = (items: unknown[]) => {
	if (items.length < MIN_TABLE_ROWS || !items.every((item) => item && typeof item === "object" && !Array.isArray(item))) return null
	const rows = items as Record<string, unknown>[]
	if (!rows.every((row) => Object.values(row).every(isPlain))) return null
	const columns = [...new Set(rows.flatMap(Object.keys))].filter((key) => rows.some((row) => !isEmpty(row[key])))
	return columns.length <= MAX_TABLE_COLUMNS ? { rows, columns } : null
}

const Fields = ({ value, depth }: { value: Record<string, unknown>; depth: number }) => {
	const [showEmpty, setShowEmpty] = useState(false)
	const entries = Object.entries(value).map(([key, item]) => [key, parseJson(item)] as const)
	const hidden = entries.filter(([, item]) => isEmpty(item)).length
	const visible = showEmpty ? entries : entries.filter(([, item]) => !isEmpty(item))
	const flat = visible.filter(([, item]) => isPlain(item) || isEmpty(item))
	const nested = visible.filter(([, item]) => !isPlain(item) && !isEmpty(item))
	return <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
		{flat.length > 0 && <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))", gap: "10px 20px", margin: 0 }}>
			{flat.map(([key, item]) => <div key={key} style={{ minWidth: 0 }}>
				<dt style={{ color: MUTED, fontSize: 12.5 }}>{humanize(key)}</dt>
				<dd style={{ margin: "3px 0 0", fontWeight: 500 }}><Scalar name={key} value={item} /></dd>
			</div>)}
		</dl>}
		{nested.map(([key, item]) => <Section key={key} title={humanize(key)} value={item} depth={depth + 1} />)}
		{hidden > 0 && <button type='button' onClick={() => setShowEmpty(!showEmpty)} style={{ justifySelf: "start", border: 0, background: "none", padding: 0, color: MUTED, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
			{showEmpty ? "Hide empty fields" : `${hidden} empty field${hidden === 1 ? "" : "s"} hidden — show`}
		</button>}
	</div>
}

const Section = ({ title, value, depth }: { title: string; value: unknown; depth: number }): ReactNode => {
	const count = Array.isArray(value) ? ` (${value.length})` : ""
	const body = <div style={{ marginTop: 10 }}><UserOverviewData value={value} depth={depth} /></div>
	const box = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: "12px 14px", background: T.surface, minWidth: 0 }
	const heading = <span style={{ fontWeight: 600, fontSize: 13.5 }}>{title}{count}</span>
	// Deep levels start collapsed so the top of the record stays scannable.
	return depth > OPEN_DEPTH
		? <details style={box}><summary style={{ cursor: "pointer" }}>{heading}</summary>{body}</details>
		: <section style={box}>{heading}{body}</section>
}

export const UserOverviewData = ({ value, depth = 0 }: { value: unknown; depth?: number }): ReactNode => {
	const [raw, setRaw] = useState(false)
	value = parseJson(value)
	if (isEmpty(value)) return <span style={{ color: MUTED }}>{Array.isArray(value) ? "No records" : "—"}</span>
	if (isPlain(value)) return <Scalar name='' value={value} />
	const toggle = depth === 0 && <button type='button' onClick={() => setRaw(!raw)} style={{ justifySelf: "end", border: `1px solid ${BORDER}`, borderRadius: 6, background: T.surface, padding: "3px 9px", fontSize: 12, color: MUTED, cursor: "pointer" }}>{raw ? "Readable view" : "Raw JSON"}</button>
	if (raw) return <div style={{ display: "grid", gap: 8 }}>{toggle}<pre style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12 }}>{JSON.stringify(value, null, 2)}</pre></div>
	let content: ReactNode
	if (Array.isArray(value)) {
		const table = asTable(value)
		content = table
			? <Paged rows={table.rows}>{(pageRows) => <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
				<thead><tr>{table.columns.map((key) => <th key={key} style={{ textAlign: "left", padding: "6px 10px", color: MUTED, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>{humanize(key)}</th>)}</tr></thead>
				<tbody>{pageRows.map((row, index) => <tr key={index}>{table.columns.map((key) => <td key={key} style={{ padding: "8px 10px", borderTop: `1px solid ${BORDER}`, verticalAlign: "top" }}><Scalar name={key} value={row[key]} /></td>)}</tr>)}</tbody>
			</table></div>}</Paged>
			: <Paged rows={value}>{(pageRows) => <div style={{ display: "grid", gap: 10 }}>{pageRows.map((item, index) => isPlain(item)
				? <div key={index}><Scalar name='' value={item} /></div>
				: <Section key={index} title={`#${index + 1}`} value={item} depth={depth + 1} />)}</div>}</Paged>
	} else content = <Fields value={value as Record<string, unknown>} depth={depth} />
	return toggle ? <div style={{ display: "grid", gap: 8, minWidth: 0 }}>{toggle}{content}</div> : content
}
